/* eslint-disable @typescript-eslint/no-explicit-any */
'use client';

import Image from 'next/image';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FaBars, FaChevronDown, FaTimes } from 'react-icons/fa';
import {
  HiOutlineArrowRightOnRectangle,
  HiOutlineUserPlus,
} from 'react-icons/hi2';

import ThemedButton from '../common/buttons/Button';
import MyAccountDropdown from './MyAccountDropdown';
import ServicesMenu from './ServicesMenu';
import UserMenu from './UserMenu';
import MobileMenu from './header/MobileMenu';
import {
  MenuBarIndicator,
  MenuBarProvider,
  useMenuBar,
  useMenuBarRegistration,
} from './header/MenuBarController';
import { PortalFlyout } from './header/PortalFlyout';
import { useFlyoutPanel } from './header/useFlyoutPanel';
import type { HeaderLink } from './header/types';
import styles from './header.module.scss';

import { useAppDispatch, useAppSelector } from '@/hooks/useRedux';
import {
  setTokenFromStorage,
  setUser,
  signOut,
} from '@/redux/auth/signin/slice';
import { imageSrc, stripBase } from '@/utils/runtimeOrg';

export type { HeaderLink } from './header/types';

interface HeaderProps {
  cfg: {
    name: string;
    logo: string;
    header?: { links?: HeaderLink[] };
  };
}

const isAbsoluteUrl = (href?: string): boolean =>
  !!href && /^https?:\/\//i.test(href);

const slugify = (s: string): string =>
  s.replace(/\s+/g, '-').replace(/[^\w-]/g, '').toLowerCase();

function NavLink({ href, label }: { href: string; label: string }) {
  const pathname = usePathname();
  const current = stripBase(pathname);
  const isActive =
    current === href || (href !== '/' && current.startsWith(href));

  return (
    <Link
      href={href}
      className={`${styles.trigger} ${isActive ? styles.active : ''}`}
    >
      {label}
    </Link>
  );
}

function HeaderDropdown({ item, isActive = false }: { item: HeaderLink; isActive?: boolean }) {
  const panelId = useMemo(() => `header-panel-${slugify(item.title)}`, [item.title]);
  const triggerId = `${panelId}-trigger`;
  const flyout = useFlyoutPanel({ id: panelId, width: 'fit', minWidth: 260, isActive });

  const children = item.children ?? [];

  return (
    <div className="relative inline-block">
      <button
        ref={flyout.triggerRef}
        id={triggerId}
        type="button"
        aria-haspopup="menu"
        aria-expanded={flyout.isOpen}
        aria-controls={flyout.isOpen ? panelId : undefined}
        onClick={flyout.toggleByPointer}
        {...flyout.triggerProps}
        className={styles.trigger}
      >
        <span>{item.title}</span>
        <FaChevronDown
          className={`text-[0.7rem] transition-transform duration-300 ${flyout.isOpen ? 'rotate-180' : ''}`}
          aria-hidden
        />
      </button>

      <PortalFlyout
        flyout={flyout}
        id={panelId}
        ariaLabel={item.title}
        ariaLabelledBy={triggerId}
      >
        <ul className="flex flex-col gap-0.5 min-w-60">
          {children.map((child, idx) => {
            const href = child.url || '#';
            const external = child.external || isAbsoluteUrl(href);

            return (
              <li key={idx}>
                {external ? (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    role="menuitem"
                    className={styles.menuitem}
                  >
                    {child.title}
                  </a>
                ) : (
                  <Link href={href} role="menuitem" className={styles.menuitem}>
                    {child.title}
                  </Link>
                )}
              </li>
            );
          })}
        </ul>
      </PortalFlyout>
    </div>
  );
}

function isPathActive(currentPath: string, href: string): boolean {
  if (!href || href === '#') return false;
  if (href === '/') return currentPath === '/';
  return currentPath === href || currentPath.startsWith(`${href}/`);
}

function SimpleNavItem({ item, isActive }: { item: HeaderLink; isActive: boolean }) {
  const id = useMemo(() => `header-link-${slugify(item.title)}`, [item.title]);
  const registerRef = useMenuBarRegistration<HTMLAnchorElement>(id, isActive);
  const href = item.url || '#';
  const external = item.external || isAbsoluteUrl(href);

  if (external) {
    return (
      <a
        ref={registerRef}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={styles.trigger}
      >
        <span>{item.title}</span>
      </a>
    );
  }

  return (
    <Link
      ref={registerRef}
      href={href}
      className={styles.trigger}
      aria-current={isActive ? 'page' : undefined}
    >
      <span>{item.title}</span>
    </Link>
  );
}

function ConfigNav({ links }: { links: HeaderLink[] }) {
  const pathname = usePathname();
  const current = stripBase(pathname);

  return (
    <ul className="flex flex-col lg:flex-row mb-2 lg:mb-0 space-y-2 lg:space-y-0 lg:space-x-6">
      {links.map((item, i) => {
        if (item.kind === 'services') {
          return (
            <li key={i} className="relative inline-block">
              <ServicesMenu />
            </li>
          );
        }

        if (Array.isArray(item.children) && item.children.length > 0) {
          const childActive = item.children.some((c) => c.url && isPathActive(current, c.url));

          return (
            <li key={i} className="relative inline-block">
              <HeaderDropdown item={item} isActive={childActive} />
            </li>
          );
        }

        const href = item.url || '#';
        const external = item.external || isAbsoluteUrl(href);
        const isActive = !external && isPathActive(current, href);

        return (
          <li key={i}>
            <SimpleNavItem item={item} isActive={isActive} />
          </li>
        );
      })}
    </ul>
  );
}

function FallbackNav() {
  return (
    <>
      <NavLink href="/" label="Home" />
      <ServicesMenu />
      <NavLink href="/support" label="Help & Support" />
      <NavLink href="/contact" label="Contact" />
      <NavLink href="/e-verify/documents" label="E-Verify" />
    </>
  );
}

function broadcastLogout() {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(
      'auth:event',
      JSON.stringify({ type: 'logout', ts: Date.now() }),
    );
    localStorage.removeItem('auth:event');
  } catch {
    // cross-tab broadcast best-effort; local signOut still runs
  }
}

function clearAuthStorage() {
  if (typeof window === 'undefined') return;
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('username');
  localStorage.removeItem('userProfile');
}

function HeaderChrome({ cfg, headerLinks, mobileMenuOpen, toggleMobileMenu, isLoggedIn, user }: {
  cfg: HeaderProps['cfg'];
  headerLinks: HeaderLink[] | undefined;
  mobileMenuOpen: boolean;
  toggleMobileMenu: () => void;
  isLoggedIn: boolean;
  user: any;
}) {
  const { barRef } = useMenuBar();

  return (
    <header
      ref={(el) => {
        barRef.current = el;
      }}
      data-header-bar
      className="sticky top-0 z-50 shadow-sm text-black"
      style={{ backgroundColor: 'var(--color-surface-header)' }}
    >
      <div
        data-header-container
        className="w-full flex items-center justify-between gap-6 px-4 sm:px-6 lg:px-8 xl:px-10 py-3"
      >
        <div className="flex items-center shrink-0">
          <Link href="/" aria-label="Home" className="block">
            {typeof cfg.logo === 'string' ? (
              <Image
                src={imageSrc(cfg.logo)}
                alt={cfg.name}
                width={480}
                height={148}
                className="h-12 sm:h-13 lg:h-14 w-auto object-contain"
                style={{ width: 'auto' }}
                priority
              />
            ) : (
              <div className="flex h-14 items-center">{cfg.logo}</div>
            )}
          </Link>
        </div>

        <button
          type="button"
          onClick={toggleMobileMenu}
          className="lg:hidden -mr-2 p-2 text-xl rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--color-accent)/40"
          aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={mobileMenuOpen}
        >
          {mobileMenuOpen ? <FaTimes /> : <FaBars />}
        </button>

        <nav
          className={`${styles.navWrapper} hidden lg:flex flex-1 items-center justify-center space-x-8 text-sm font-medium`}
          aria-label="Main"
        >
          {Array.isArray(headerLinks) && headerLinks.length > 0 ? (
            <ConfigNav links={headerLinks} />
          ) : (
            <FallbackNav />
          )}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          {isLoggedIn ? (
            <div className="flex items-center gap-2">
              <MyAccountDropdown />
              <UserMenu user={user} />
            </div>
          ) : (
            <>
              <ThemedButton
                variant="outlined"
                theme="accent"
                text="Log In"
                href="/account/signin"
                icon={<HiOutlineArrowRightOnRectangle />}
              />
              <ThemedButton
                theme="primary"
                text="Sign Up"
                href="/account/signup"
                icon={<HiOutlineUserPlus />}
              />
            </>
          )}
        </div>
      </div>

      <MenuBarIndicator />
    </header>
  );
}

export default function Header({ cfg }: HeaderProps) {
  const router = useRouter();
  const dispatch = useAppDispatch();

  const headerLinks = cfg?.header?.links;

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);

  const { token, user } = useAppSelector((s: any) => s.auth?.signin ?? {});
  const isLoggedIn = Boolean(token?.access);
  const username = user?.first_name || user?.username || 'User';

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const access = localStorage.getItem('accessToken');
    const refresh = localStorage.getItem('refreshToken');
    if (access && refresh) {
      dispatch(setTokenFromStorage({ access, refresh }));
    }

    const raw = localStorage.getItem('userProfile');
    if (raw) {
      try {
        dispatch(setUser(JSON.parse(raw)));
      } catch {
        // stored profile corrupt; user will re-login
      }
    }
  }, [dispatch]);

  const toggleMobileMenu = useCallback(() => setMobileMenuOpen((v) => !v), []);
  const closeMobileMenu = useCallback(() => setMobileMenuOpen(false), []);
  const toggleServices = useCallback(() => setServicesOpen((v) => !v), []);

  const handleLogout = useCallback(() => {
    clearAuthStorage();
    broadcastLogout();
    dispatch(signOut());
    router.push('/account/signin');
  }, [dispatch, router]);

  return (
    <MenuBarProvider>
      <HeaderChrome
        cfg={cfg}
        headerLinks={headerLinks}
        mobileMenuOpen={mobileMenuOpen}
        toggleMobileMenu={toggleMobileMenu}
        isLoggedIn={isLoggedIn}
        user={user}
      />
      <MobileMenu
        logo={imageSrc(cfg.logo)}
        name={cfg.name}
        open={mobileMenuOpen}
        onClose={closeMobileMenu}
        servicesOpen={servicesOpen}
        toggleServices={toggleServices}
        isLoggedIn={isLoggedIn}
        username={username}
        handleLogout={handleLogout}
        links={headerLinks}
      />
    </MenuBarProvider>
  );
}
